"use client";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { createContext, useContext, type ReactNode, Suspense, useEffect } from "react";
import { identify } from "@/lib/identify";

interface UserContextType {
  user: Doc<"users">;
  team: Doc<"teams">;
  isAuthenticated: boolean;
}

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: ReactNode;
}

function UserProviderInner({ children }: UserProviderProps) {
  const userData = useQuery(api.authentication.queries.getMe, {});
  const value = {
    user: userData?.user ?? ({} as Doc<"users">),
    team: userData?.team ?? ({} as Doc<"teams">),
    isAuthenticated: !!userData?.user,
  };
  useEffect(() => {
    const identifyUser = async () => {
      if (userData?.user) {
        await identify(userData.user);
      }
    };
    identifyUser();
  }, [userData?.user]);
  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function UserProvider({ children }: UserProviderProps) {
  return (
    <Suspense>
      <UserProviderInner>{children}</UserProviderInner>
    </Suspense>
  );
}

export function withUser(): UserContextType {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("withUser must be used within a UserProvider");
  }
  return context;
}
